// src/components/AdminUsersTab.jsx
import React, { useState } from 'react';
import { userService } from '../../services/api';

export default function AdminUsersTab({ users, setUsers, actionLoading, setActionLoading, setError }) {
    const [isCreating, setIsCreating] = useState(false);
    const [newUserData, setNewUserData] = useState({ name: '', email: '', password: '', role: 'citizen' });

    const handleUpdateRole = async (userId, newRole) => {
        if (actionLoading) return;
        setActionLoading(true);
        try {
            await userService.updateUserRole(userId, newRole);
            
            setUsers(prevUsers => 
                prevUsers.map(u => u.id === userId ? { ...u, role: newRole } : u)
            );
            setError(null);
        } catch (err) {
            console.error('Error updating role:', err);
            setError('Error al actualizar el rol.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (actionLoading) return;

        if (window.confirm('¿Estás seguro de querer eliminar este usuario? Esta acción es irreversible.')) {
            setActionLoading(true);
            try {
                await userService.deleteUser(userId);
                
                setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
                setError(null);
            } catch (err) {
                console.error('Error deleting user:', err);
                setError('Error al eliminar el usuario.');
            } finally {
                setActionLoading(false);
            }
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (actionLoading) return;
        setActionLoading(true);
        try {
            await userService.createUser(newUserData);
            
            // Recargar la lista para incluir el nuevo usuario y limpiar el formulario
            // (Alternativamente, podrías insertar el usuario en el estado local si la respuesta es completa)
            setNewUserData({ name: '', email: '', password: '', role: 'citizen' });
            setIsCreating(false);
            setError(null);
            
            // Llama a una función de recarga pasada desde el Dashboard si es necesario
            alert('Usuario creado exitosamente. Recargue la pestaña para verlo.');

        } catch (err) {
            console.error('Error creating user:', err);
            setError('Error al crear el usuario.');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="mt-4">
            <h4 className="text-lg font-semibold mb-3">Gestión de Usuarios</h4>

            <button
                onClick={() => setIsCreating(!isCreating)}
                className="mb-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none disabled:opacity-50"
                disabled={actionLoading}
            >
                {isCreating ? 'Cancelar' : 'Crear Nuevo Usuario'}
            </button>

            {isCreating && (
                <form onSubmit={handleCreateUser} className="mb-6 p-4 border rounded bg-gray-50 space-y-3 max-w-lg">
                    <input
                        type="text"
                        placeholder="Nombre"
                        value={newUserData.name}
                        onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                        required
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md"
                        disabled={actionLoading}
                    />
                    <input
                        type="email"
                        placeholder="Email"
                        value={newUserData.email}
                        onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                        required
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md"
                        disabled={actionLoading}
                    />
                    <input
                        type="password"
                        placeholder="Contraseña"
                        value={newUserData.password}
                        onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                        required
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md"
                        disabled={actionLoading}
                    />
                    <select
                        value={newUserData.role}
                        onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md"
                        disabled={actionLoading}
                    >
                        <option value="citizen">Ciudadano</option>
                        <option value="editor">Editor</option>
                        <option value="admin">Administrador</option>
                    </select>
                    <button
                        type="submit"
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[color:var(--primary)] hover:bg-[color:var(--primary-dark)] disabled:opacity-50"
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Creando...' : 'Guardar Usuario'}
                    </button>
                </form>
            )}


            {actionLoading && <div className="text-center text-[color:var(--primary)] mb-4">Procesando acción...</div>}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    <select
                                        value={user.role}
                                        onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm sm:text-sm"
                                        disabled={actionLoading}
                                    >
                                        <option value="citizen">Ciudadano</option>
                                        <option value="editor">Editor</option>
                                        <option value="admin">Administrador</option>
                                    </select>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                        onClick={() => handleDeleteUser(user.id)}
                                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                        disabled={actionLoading}
                                    >
                                        Eliminar
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}